(() => {
  if (window.ManatorskNotify?.version) return;

  const state = {
    lastFocusedElement: null,
    closeTimer: null,
  };

  function getDialog() {
    const dialog = document.querySelector('[data-manatorsk-notify-dialog]');

    if (dialog && dialog.dataset.notifyBound !== 'true') {
      dialog.dataset.notifyBound = 'true';
      dialog.addEventListener('close', restoreFocus);
    }

    return dialog;
  }

  function getConfig() {
    const configElement = document.querySelector('[data-manatorsk-notify-config]');

    try {
      return JSON.parse(configElement?.textContent || '{}');
    } catch (error) {
      console.error('Manatorsk notify: invalid configuration.', error);
      return {};
    }
  }

  function open(detail = {}) {
    const dialog = getDialog();
    if (!dialog) {
      console.error('Manatorsk notify: dialog markup was not found.');
      return false;
    }

    const config = getConfig();
    const labels = config.labels || {};
    const productTitle = String(detail.productTitle || detail.variantTitle || 'Produkten');
    const titleTemplate = labels.titleTemplate || 'Bevaka [[variant]]';
    const title = titleTemplate.replace(/\[\[variant\]\]/gi, productTitle);

    const titleElement = dialog.querySelector('[data-notify-title]');
    const emailElement = dialog.querySelector('[data-notify-email]');
    const variantIdElement = dialog.querySelector('[data-notify-variant-id]');
    const variantTitleElement = dialog.querySelector('[data-notify-variant-title]');
    const submitElement = dialog.querySelector('[data-notify-submit]');
    const submitLabelElement = dialog.querySelector('[data-notify-submit-label]');

    window.clearTimeout(state.closeTimer);
    state.lastFocusedElement = document.activeElement;

    if (titleElement) titleElement.textContent = title;
    if (variantIdElement) variantIdElement.value = detail.variantId || '';
    if (variantTitleElement) variantTitleElement.value = productTitle;

    if (emailElement && !emailElement.value && config.defaultEmail) {
      emailElement.value = config.defaultEmail;
    }

    if (submitElement) {
      submitElement.disabled = false;
      submitElement.dataset.loading = 'false';
    }
    if (submitLabelElement) submitLabelElement.textContent = labels.send || 'Bevaka produkten';

    clearMessage(dialog);

    if (typeof dialog.showModal === 'function') {
      if (!dialog.open) dialog.showModal();
    } else {
      dialog.setAttribute('open', '');
    }

    window.requestAnimationFrame(() => emailElement?.focus());
    return true;
  }

  function close(dialog = getDialog()) {
    if (!dialog) return;

    window.clearTimeout(state.closeTimer);

    if (typeof dialog.close === 'function' && dialog.open) {
      dialog.close();
    } else {
      dialog.removeAttribute('open');
      restoreFocus();
    }
  }

  function restoreFocus() {
    if (state.lastFocusedElement instanceof HTMLElement) {
      state.lastFocusedElement.focus({ preventScroll: true });
    }
    state.lastFocusedElement = null;
  }

  function clearMessage(dialog) {
    const messageElement = dialog.querySelector('[data-notify-message]');
    if (!messageElement) return;

    messageElement.hidden = true;
    messageElement.textContent = '';
    messageElement.className = 'manatorsk-notify-dialog__message';
  }

  function showMessage(dialog, message, type) {
    const messageElement = dialog.querySelector('[data-notify-message]');
    if (!messageElement) return;

    messageElement.textContent = message;
    messageElement.className = `manatorsk-notify-dialog__message is-${type}`;
    messageElement.hidden = false;
  }

  function normalizeVariantId(value) {
    return String(value || '').split('/').pop();
  }

  async function submit(form) {
    const dialog = form.closest('[data-manatorsk-notify-dialog]');
    if (!dialog || !form.reportValidity()) return;

    const config = getConfig();
    const labels = config.labels || {};
    const emailElement = form.querySelector('[data-notify-email]');
    const variantIdElement = form.querySelector('[data-notify-variant-id]');
    const variantTitleElement = form.querySelector('[data-notify-variant-title]');
    const submitElement = form.querySelector('[data-notify-submit]');
    const submitLabelElement = form.querySelector('[data-notify-submit-label]');

    const email = emailElement?.value.trim() || '';
    const variantId = normalizeVariantId(variantIdElement?.value);
    const variantTitle = variantTitleElement?.value || '';

    if (!variantId) {
      showMessage(dialog, labels.error || 'Kunde inte hitta produktvarianten.', 'error');
      return;
    }

    clearMessage(dialog);
    submitElement.disabled = true;
    submitElement.dataset.loading = 'true';
    submitLabelElement.textContent = labels.sending || 'Skickar';

    try {
      const response = await fetch(config.endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          email,
          variantId,
          variantTitle,
        }),
      });

      const responseBody = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(responseBody.error || responseBody.message || labels.error);
      }

      showMessage(
        dialog,
        responseBody.message || labels.success || 'Tack! Vi meddelar dig när produkten går att beställa.',
        'success',
      );

      emailElement.value = '';
      state.closeTimer = window.setTimeout(() => close(dialog), 1700);
    } catch (error) {
      console.error('Manatorsk notify: request failed.', error);
      showMessage(dialog, error.message || labels.error || 'Något gick fel. Försök igen.', 'error');
    } finally {
      submitElement.disabled = false;
      submitElement.dataset.loading = 'false';
      submitLabelElement.textContent = labels.send || 'Bevaka produkten';
    }
  }

  document.addEventListener('manatorsk:notify-request', (event) => {
    const detail = event.detail || {};

    // Let the originating component use its product-page fallback if the API
    // response did not contain a usable variant ID.
    if (!detail.variantId) return;

    event.preventDefault();
    open(detail);
  });

  document.addEventListener('click', (event) => {
    const closeButton = event.target.closest('[data-notify-close]');
    if (closeButton) {
      event.preventDefault();
      close(closeButton.closest('[data-manatorsk-notify-dialog]'));
      return;
    }

    const dialog = event.target.closest('[data-manatorsk-notify-dialog]');
    if (dialog && event.target === dialog) {
      close(dialog);
      return;
    }

    const trigger = event.target.closest('[data-manatorsk-notify-trigger]');
    if (!trigger) return;

    event.preventDefault();
    open({
      variantId: trigger.dataset.variantId,
      productTitle: trigger.dataset.productTitle,
      productUrl: trigger.dataset.productUrl,
    });
  });

  document.addEventListener('submit', (event) => {
    const form = event.target.closest('[data-notify-form]');
    if (!form) return;

    event.preventDefault();
    submit(form);
  });

  window.ManatorskNotify = {
    version: '1.0.0',
    open,
    close,
  };
})();